import React, { useState } from "react";
import { Input } from "@/app/global-components/input";
import { Button, MenuItem, OutlinedInput, Select } from "@mui/material";
import { useSelector } from "react-redux";
import { RootState } from "@/store/type";
// import { Autocomplete } from "@mui/material";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import { showToast } from "@/app/global-components/toaster";
import useRegisterCompany from "@/hooks/use-register-company";
import { LANG } from "@/types";
import { CategoryOption } from "@/pages/seller/articles";

interface AddNewItemProps {
  isOpen: boolean;
  Categoris: CategoryOption[];
  onClose: () => void;
  onSave: (newItem: any) => void;
  isLoading: boolean;
  error: string | null;
}
const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
export const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};
const AddNewItem: React.FC<AddNewItemProps> = ({
  Categoris,
  isOpen,
  onClose,
  onSave,
  isLoading,
  error,
}) => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const authUser = useSelector((state: RootState) => state.auth.authUser);
  const myCompany = useSelector((state: RootState) => state.auth.myCompany);
  const { getCompanyMine } = useRegisterCompany();
  const [newItem, setNewItem] = useState({
    name: "",
    description: "",
    price: 0,
    vat: "",
    stock: 0,
    tags: [] as string[],
    image: null as File | null,
    imagePreview: "",
    type: "Machine",
  });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setNewItem((prev) => ({
      ...prev,
      [name]: name === "price" || name === "stock" ? +value : value,
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setNewItem((prev) => ({
        ...prev,
        image: file,
        imagePreview: URL.createObjectURL(file),
      }));
    }
  };
  const [filterCategories, setFilterCategories] = useState<string[] | string>(
    []
  );

  const handleSave = () => {
    if (!newItem.name.trim()) {
      showToast(t("Alert.NameRequired"), "warning");
      return;
    }
    if (!newItem.type) {
      showToast(t("Alert.TypeRequired"), "warning");
      return;
    }

    const payload = {
      ...newItem,
      price: Number(newItem.price),
      vat: newItem.vat ? Number(newItem.vat) : null,
      stock: Number(newItem.stock),
      categories: filterCategories,
      attachments: [],
      companyId: myCompany.id,
      userId: authUser.id,
      ItemNR: myCompany.itemNr + 1,
    };

    onSave(payload);
    getCompanyMine();
    onClose();
    setNewItem({
      name: "",
      description: "",
      price: 0,
      vat: "",
      stock: 0,
      tags: [],
      image: null,
      imagePreview: "",
      type: "Machine",
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-4xl space-y-3">
        <h3 className="text-2xl font-bold mb-4 text-center">{t("item.add")}</h3>
        <div className="grid grid-cols-12 gap-4 space-y-3">
          <div className="col-span-12 space-y-3">
            <label className="block text-sm font-medium">
              {t("item.fields.name")}*
            </label>
            <Input
              type="text"
              name="name"
              placeholder={t("item.fields.name")}
              value={newItem.name}
              onChange={handleInputChange}
              className="w-full py-2 px-3"
            />
          </div>
          <div className="col-span-12 space-y-3">
            <label className="block text-sm font-medium">
              {t("item.fields.description")}*
            </label>
            <Input
              type="text"
              name="description"
              placeholder={t("item.fields.description")}
              value={newItem.description}
              onChange={handleInputChange}
              className="w-full py-2 px-3"
            />
          </div>
          <div className="col-span-3 space-y-3">
            <label className="block text-sm font-medium">
              {t("item.fields.price")}*
            </label>
            <Input
              type="number"
              name="price"
              placeholder={t("item.fields.price")}
              value={newItem.price}
              onChange={handleInputChange}
              className="w-full py-2 px-3"
            />
          </div>
          <div className="col-span-3 space-y-3">
            <label className="block text-sm font-medium">
              {t("item.fields.vat")}
            </label>
            <select
              name="vat"
              value={newItem.vat}
              onChange={handleInputChange}
              className="w-full py-2 px-3 border rounded"
            >
              <option value="0">0%</option>
              <option value="15">15%</option>
              <option value="">{t("item.without")}</option>
            </select>
          </div>
          <div className="col-span-3 space-y-3">
            <label className="block text-sm font-medium">
              {t("item.fields.stock")}
            </label>
            <Input
              type="number"
              name="stock"
              placeholder={t("item.fields.stock")}
              value={newItem.stock}
              onChange={handleInputChange}
              className="w-full py-2 px-3"
            />
          </div>
          <div className="col-span-3 space-y-3">
            <label className="block text-sm font-medium">
              {t("item.fields.type")}
            </label>
            <select
              name="type"
              value={newItem.type}
              onChange={handleInputChange}
              className="w-full py-2 px-3 border rounded"
            >
              <option value="Machine">{t("item.types.machine")}</option>
              <option value="Service">{t("item.types.service")}</option>
              <option value="Installation">
                {t("item.types.installation")}
              </option>
            </select>
          </div>
          <div className="col-span-12 space-y-3">
            <label className="block text-sm font-medium">
              {t("item.fields.categories")}
            </label>
            <div className="flex items-center space-x-2">
              {/* <Autocomplete
                multiple
                freeSolo
                fullWidth
                options={Categoris.filter((category) =>
                  myCompany.categoriesIds.includes(category.id)
                ).map((category) => category.name)}
                value={newItem.tags}
                onChange={(event, value) => {
                  setNewItem((prev) => ({
                    ...prev,
                    tags: value,
                  }));
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={index}
                      label={option}
                      color="primary"
                      variant="outlined"
                      size="small"
                      sx={{
                        margin: "2px",
                        backgroundColor: "#e3f2fd", // Light blue background
                        color: "#0d47a1", // Dark blue text
                        fontWeight: "bold",
                      }}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder="Add Category"
                    variant="outlined"
                  />
                )}
              /> */}
              <Select
                displayEmpty
                id="categorie-multiple-name"
                input={<OutlinedInput />}
                inputProps={{ "aria-label": "Without label" }}
                className="h-10 bg-white w-36 md:w-64 xl:w-80 border !border-zinc-500"
                sx={{
                  "& .MuiSelect-select": {
                    paddingRight: "10px !important",
                  },
                  "& .MuiSelect-icon": {
                    left: locale === LANG.EN ? "unset" : 0,
                    marginLeft: 0,
                    right: locale === LANG.EN ? 0 : "unset",
                  },
                }}
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return <em>{t("categories.select")}</em>;
                  }
                  const value = Categoris.filter((value) =>
                    selected.includes(value.value)
                  ).map((item) => item.label);
                  return value.join(", ");
                }}
                value={filterCategories}
                onChange={(e) => {
                  setFilterCategories(e.target.value);
                }}
                MenuProps={MenuProps}
              >
                <MenuItem disabled value="">
                  <em>{t("categories.select")}</em>
                </MenuItem>
                {Categoris.map((name) => (
                  <MenuItem
                    className="!mb-2"
                    sx={{
                      "&.Mui-selected": {
                        backgroundColor: "#000",
                        borderRadius: 15,
                        color: "#fff",
                        fontWeight: "600",
                      },
                      "&:hover.MuiMenuItem-root": {
                        backgroundColor: "#000",
                        borderRadius: 15,
                        color: "#fff",
                      },
                    }}
                    key={name.value}
                    value={name.value}
                    // style={getStyles(name, personName, theme)}
                  >
                    <span>{name.label}</span>
                  </MenuItem>
                ))}
              </Select>
            </div>
          </div>
          <div className="col-span-12 space-y-3">
            <label className="block text-sm font-medium">
              {t("item.fields.image")}
            </label>
            <input
              type="file"
              name="image"
              onChange={handleFileChange}
              className="w-full py-2 px-3 border rounded"
            />
            {newItem.imagePreview && (
              <img
                src={newItem.imagePreview}
                alt="Preview"
                className="w-40 h-40 object-cover mt-2 rounded-lg"
              />
            )}
          </div>
        </div>
        {isLoading && <p>{t("item.saving")}</p>}
        {error && <p className="text-red-500">Error: {error}</p>}
        <div className="pt-10 flex gap-5 justify-end items-center">
          <Button
            onClick={onClose}
            variant="outlined"
            color="secondary"
            sx={{
              mx: 1,
            }}
          >
            {t("actions.cancel")}
          </Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            {t("actions.save")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddNewItem;
